import {
    Component,
    OnInit,
    ChangeDetectorRef,
    HostListener,
    OnDestroy
} from '@angular/core'
import {
    ActivatedRoute,
    Router,
    NavigationEnd,
    RouterOutlet
} from '@angular/router'
import { CommonModule } from '@angular/common'
import { Title } from '@angular/platform-browser'

import { TranslateModule } from '@ngx-translate/core'
import { filter, map, mergeMap } from 'rxjs'

import { SharedModule } from '@@shared/shared.module'

import { LOCAL_STORAG_KEYS } from '@@constants/local-storage-keys'

import { WebsocketService } from '@@services/websocket.service'
import { AuthService } from '@@services/auth.service'
import { FingerPrintService } from '@@services/fingerprint.service'

import { environment } from '../../../environments/environment'

import { PortalHeaderComponent } from '../portal-header/portal-header.component'
import { PortalFooterComponent } from '../portal-footer/portal-footer.component'
import { SidenavComponent } from '../sidenav/sidenav.component'

interface NotificationData {
    data: {
        customerMemberships: {
            membership: {
                idClient: any //
                balance: number
            }
            idClient: any //
            balance: number
        }
        membership: {
            idClient: any //
            balance: number
        }
        idClient: any //
        balance: number
    }
    status: string
    type: any
    user: string
    statustemp: string
    message: string
    openCheckinTime: string
}

@Component({
    standalone: true,
    imports: [
        SharedModule,
        CommonModule,
        RouterOutlet,
        TranslateModule,
        PortalHeaderComponent,
        PortalFooterComponent,
        SidenavComponent
    ],
    selector: 'portal-layout',
    templateUrl: './portal-layout.component.html',
    styleUrls: ['./portal-layout.component.scss']
})
export class PortalLayoutComponent implements OnInit, OnDestroy {
    public portal = environment.portal

    public app: any
    public headerThemes: any
    public changeHeader: any
    public sidenavThemes: any
    public changeSidenav: any
    public headerSelected: any
    public sidenavSelected: any
    public searchActived: any
    public searchModel: any

    public theme: string | null = localStorage.getItem(LOCAL_STORAG_KEYS.THEME)
    public sidenavOpened = false
    public themeDark = true

    isMobile: boolean = false
    portalHeader = false
    portalFooter = false
    fromApp = false
    isEmbedded = false

    // WS notification
    notification: string = ''
    notification_idClient: any
    notification_Active: boolean = false
    notification_Title: string = ''
    notification_User: string = ''
    notification_Status: any = ''
    notification_Message: string = ''
    notification_Balance: number = 0
    notification_Time: string = ''
    notification_Membership: any
    notification_customerMemberships: any
    notification_timeout: any
    currency = localStorage.getItem(LOCAL_STORAG_KEYS.CURRENCY)!
    statusObject: {
        [key: string]: string
    } = {
        authorizedAccess: 'checkin-authorized',
        openCheckin: 'checkin-alert',
        noOpenCheckin: 'checkin-alert',
        clientDeleted: 'checkin-alert',
        unidentifiedAccess: 'checkin-alert',
        clientNoLongerInSystem: 'checkin-alert',
        invalidMembership: 'checkin-alert'
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
        private ws: WebsocketService,
        private titleService: Title
    ) {
        this.app = {
            layout: {
                sidePanelOpen: false,
                isMenuOpened: true,
                themeConfigOpen: false,
                rtlActived: false,
                searchActived: false
            }
        }

        this.headerThemes = [
            'header-default',
            'header-primary',
            'header-info',
            'header-success',
            'header-danger',
            'header-dark'
        ]

        this.changeHeader = (headerTheme: string) => {
            this.headerSelected = headerTheme
        }

        this.sidenavThemes = ['sidenav-default', 'side-nav-dark']
        this.changeSidenav = (sidenavTheme: string) => {
            this.sidenavSelected = sidenavTheme
        }

        try {
            this.isEmbedded = window.self !== window.top
        } catch (e) {
            this.isEmbedded = true
        }

        this.router.events
            .pipe(
                filter((event: any) => event instanceof NavigationEnd),
                map(() => this.route),
                map((r: ActivatedRoute) => {
                    while (r.firstChild) {
                        r = r.firstChild
                    }
                    return r
                }),
                mergeMap((r: ActivatedRoute) => r.data)
            )
            .subscribe((event: any) => {
                this.titleService.setTitle(event.title)
                this.portalHeader =
                    event.portalHeader === true
                        ? (this.portalHeader = true)
                        : (this.portalHeader = false)
                this.portalFooter =
                    event.portalFooter === true
                        ? (this.portalFooter = true)
                        : (this.portalFooter = false)
                if (this.isEmbedded) {
                    this.portalHeader = false
                    this.portalFooter = false
                }
                if (event.isClient) {
                    localStorage.setItem(LOCAL_STORAG_KEYS.IS_CLIENT, 'true')
                } else {
                    localStorage.removeItem(LOCAL_STORAG_KEYS.IS_CLIENT)
                }
            })
    }

    ngOnInit() {
        localStorage.setItem(LOCAL_STORAG_KEYS.THEME, 'white')

        if (this.theme === 'dark') {
            this.themeDark = true
        } else {
            this.themeDark = false
        }

        this.updateIsMobile()
        window.addEventListener('resize', this.updateIsMobile.bind(this))

        this.route.queryParams.subscribe((params) => {
            if (params['application']) {
                this.fromApp = true
            }
        })

        // to separate & update
        localStorage.removeItem(LOCAL_STORAG_KEYS.ROUTE_DATA)
        if (this.authService.isLoggedIn()) {
            const idTenant = localStorage.getItem(LOCAL_STORAG_KEYS.TENANT_ID)
            if (idTenant && +idTenant > 0) {
                this.authService.getTenantDetails(true, true)
            }

            this.authService.performSynchronizeUser()
        }

        this.notification_Active = false
        this.runWs()
    }

    @HostListener('document:ws-member-notification', [
        '$event',
        '$event.detail'
    ])
    wsNotification(_event: any, detail: { data: NotificationData }) {
        const data = detail.data
        this.notificationAdd(data)
    }

    ngOnDestroy() {
        window.removeEventListener('resize', this.updateIsMobile.bind(this))
    }

    updateIsMobile() {
        this.isMobile = this.getIsMobile()
        this.cdr.detectChanges()
    }

    getIsMobile() {
        const width = Math.max(
            document.documentElement.clientWidth,
            window.innerWidth || 0
        )
        return width <= 990
    }

    sidemenuToggle(overlayDiv: { style: { display: string } }) {
        this.sidenavOpened = !this.sidenavOpened
        if (this.sidenavOpened) {
            overlayDiv.style.display = 'block'
        } else {
            overlayDiv.style.display = 'none'
        }
    }

    runWs() {
        if (FingerPrintService.isFingerPrintEnabled()) {
            this.ws.userCheckinListener()
        }
    }

    notificationAdd(data: NotificationData) {
        if (this.notification_timeout) {
            clearInterval(this.notification_timeout)
        }
        this.notification_Active = true
        this.notification_idClient =
            data.data && data.data.idClient ? data.data.idClient : false
        this.notification_Title =
            (data.status === 'authorizedAccess'
                ? 'layout.portal-layout.succeeded'
                : 'layout.portal-layout.failed') +
            ' ' +
            (data.type ? data.type : 'Unidentified')
        this.notification_User = data.user
        this.notification_Status = data.status ? data.status : data.statustemp
        this.notification_Message = data.message
        this.notification_Time = data.openCheckinTime
        this.notification_Balance =
            data.data && data.data.balance ? data.data.balance : 0
        this.notification_Membership =
            data.data && data.data.membership ? data.data.membership : false
        this.notification_customerMemberships =
            data.data && data.data.customerMemberships
                ? data.data.customerMemberships
                : false

        this.notification_timeout = setTimeout(() => {
            this.notification_Active = false
        }, 40000)
    }

    notificationClose(event: MouseEvent) {
        this.notification_Active = false
        const tttt = event.currentTarget as Element
        tttt.parentElement?.remove()
    }

    // to fix
    checkBalance(balance: number) {
        let classSet
        if (balance >= 0) {
            classSet = 'checkin-authorized'
        } else {
            classSet = 'checkin-alert'
        }
        return classSet
    }

    // to fix
    checkMembership(membership: any) {
        let classSet
        if (membership.isExpired) {
            classSet = 'checkin-alert'
        } else {
            classSet = 'checkin-authorized'
        }
        return classSet
    }
}
